import HttpUtil, { PageResponse, ResultResponse } from '@/api'
import { GetPositionLevelResponse, ListPositionLevelResponse } from '@/api/system/positionLevel'
import { GetPositionResponse, ListPositionResponse } from '@/api/system/position'
import { ListJobResponse } from '@/api/system/job'
import { ListRoleResponse } from '@/api/system/role'
import { FileResponse } from '@/api/global/file'

export type ExternalGetTokenRequest = {
    account: string
    password: string
    captchaCode: string
    captchaVerifyCode: string
}

export type ExternalGetTokenResponse = {
    accessToken: string
    refreshToken: string
    expiresIn: number
}

export type CurrentEmployeePositionLevelResponse = {
    // 职级 ID
    id: string
    // 名称
    name: string
    // 编码
    code: string
}
export type CurrentEmployeePositionResponse = {
    // 职务 ID
    id: string
    // 名称
    name: string
    // 编码
    code: string
}

export type CurrentDepartmentResponse = {
    // 部门 ID
    id: string
    // 名称
    name: string
    // 编码
    code: string
    // 父级 ID
    parentId: string
    // 部门树形结构
    tree?: CurrentDepartmentResponse
}

export type CurrentEmployeeJobResponse = {
    // 职位 ID
    id: string
    // 名称
    name: string
    // 编码
    code: string
}

export type CurrentEmployeeRoleResponse = {
    // 角色 ID
    id: string
    // 名称
    name: string
    // 编码
    code: string
}

export type GetCurrentEmployeeResponse = {
    // 员工 ID
    id: string
    // 工号
    workNumber: number
    // 手机号码
    mobilePhone: string
    // 电子邮箱
    email: string
    // 姓名
    name: string
    // 昵称
    nickname: string
    // 头像文件 ID
    avatarFileId: string
    // 头像文件 URL
    avatarFile: FileResponse
    // 职级 ID
    positionLevelId: string
    // 职级
    positionLevel: CurrentEmployeePositionLevelResponse
    // 职务 ID
    positionId: string
    // 职务
    position: CurrentEmployeePositionResponse
    // 职位 ID 数组
    jobIdList: string[]
    // 职位数组
    jobList: CurrentEmployeeJobResponse[]
    // 部门 ID
    departmentId: string
    // 部门
    department: CurrentDepartmentResponse
    // 角色 ID 数组
    roleIdList: string[]
    // 角色数组
    roleList: CurrentEmployeeRoleResponse[]
    // 角色编码数组
    roleCodeList: string[]
    // 角色权限编码数组
    rolePermissionCodeList: string[]
    // 树形角色视图数组
    treeRoleViewList: TreeCurrentRoleViewResponse[]
}

export type TreeCurrentRoleViewResponse = {
    id: string
    name: string
    path: string
    icon: string
    code: string
    type: number
    parentId: string
    isCategory: number
    children: TreeRoleViewResponse[]
}

export type TreeRoleViewResponse = {
    id: string
    name: string
    path: string
    icon: string
    code: string
    type: number
    parentId: string
    isCategory: number
    children: TreeRoleViewResponse[]
}

// 系统-员工
export type AddEmployeeRequest = {
    // 状态：1=启用，2=禁用
    status?: number
    // 手机号码
    mobilePhone?: string
    // 电子邮箱
    email?: string
    // 姓名
    name?: string
    // 昵称
    nickname?: string
    // 头像文件 ID
    avatarFileId?: string
    // 职级 ID
    positionLevelId?: string
    // 职务 ID
    positionId?: string
    // 部门 ID
    departmentId?: string
    // 职位 ID 数组
    jobIdList?: string[]
    // 职位 ID 数组
    roleIdList?: string[]
    // 备注
    remark?: string
}

// 系统-员工
export type UpdateEmployeeRequest = {
    // 员工 ID
    id?: string
    // 状态：1=启用，2=禁用
    status?: number
    // 工号
    workNumber?: string
    // 手机号码
    mobilePhone?: string
    // 电子邮箱
    email?: string
    // 姓名
    name?: string
    // 昵称
    nickname?: string
    // 头像文件 ID
    avatarFileId?: string
    // 职级 ID
    positionLevelId?: string
    // 职务 ID
    positionId?: string
    // 部门 ID
    departmentId?: string
    // 职位 ID 数组
    jobIdList?: string[]
    // 角色 ID 数组
    roleIdList?: string[]
    // 备注
    remark?: string
}

export type PageEmployeeRequest = {
    // 当前页
    pageNumber?: number
    // 每页大小
    pageSize?: number
    // 员工 ID 数组
    idList?: string[]
    // 关键字：姓名/工号/手机号码
    keyword?: string
}

// 系统-员工
export type PageEmployeeResponse = {
    // 员工 ID
    id: string
    // 状态：1=启用，2=禁用
    status: number
    // 姓名
    name: string
    // 工号
    workNumber: string
    // 手机号码
    mobilePhone: string
    // 电子邮箱
    email: string
    // 头像文件
    avatarFile: FileResponse
    // 职级 ID
    positionLevelId: string
    // 职级
    positionLevel: ListPositionLevelResponse
    // 职务 ID
    positionId: string
    // 职务
    position: ListPositionResponse
    // 职位 ID 数组
    jobIdList: string
    // 职位数组
    jobList: ListJobResponse[]
    // 部门 ID
    departmentId: string
    // 备注
    remark: string
    // 创建时间
    createTime: number
    // 更新时间
    updateTime: number
}

export type DeleteEmployeeRequest = {
    // 员工 ID
    id: string
}

export type GetEmployeeRequest = {
    // 员工 ID
    id: string
}

export type GetEmployeeResponse = {
    // 员工 ID
    id: string
    // 状态：1=启用，2=禁用
    status: number
    // 工号
    workNumber: string
    // 手机号码
    mobilePhone: string
    // 电子邮箱
    email: string
    // 姓名
    name: string
    // 昵称
    nickname: string
    // 头像文件
    avatarFile: FileResponse
    // 职级 ID
    positionLevelId: string
    // 职级
    positionLevel: GetPositionLevelResponse
    // 职务 ID
    positionId: string
    // 职务
    position: GetPositionResponse
    // 职位 ID 数组
    jobIdList: string[]
    // 职位数组
    jobList: ListJobResponse[]
    // 部门 ID
    departmentId: string
    // 角色 ID 数组
    roleIdList: string[]
    // 角色数组
    roleList: ListRoleResponse[]
    // 备注
    remark: string
    // 创建时间
    createTime: number
    // 更新时间
    updateTime: number
}

export type ListEmployeeResponse = {
    // 员工 ID
    id: string
    // 状态：1=启用，2=禁用
    status: number
    // 工号
    workNumber: string
    // 手机号码
    mobilePhone: string
    // 电子邮箱
    email: string
    // 姓名
    name: string
    // 昵称
    nickname: string
    // 头像文件
    avatarFile: FileResponse
    // 职级 ID
    positionLevelId: number
    // 职级
    positionLevel: GetPositionLevelResponse
    // 职务 ID
    positionId: number
    // 职务
    position: GetPositionResponse
    // 职位 ID 数组
    jobIdList: string[]
    // 职位数组
    jobList: ListJobResponse[]
    // 部门 ID
    departmentId: string
    // 角色 ID 数组
    roleIdList: string[]
    // 角色数组
    roleList: ListRoleResponse[]
    // 备注
    remark: string
    // 创建时间
    createTime: number
    // 更新时间
    updateTime: number
}

export type ModifyEmployeePasswordRequest = {
    // 员工 ID
    id?: string
    // 密码
    password?: string
}

export type ModifyCurrentEmployeePasswordRequest = {
    // 员工 ID
    id: string
    // 原密码
    oldPassword: string
    // 新密码
    newPassword: string
    // 重复新密码
    confirmNewPassword: string
}

// 系统-员工
export type UpdateCurrentEmployeeRequest = {
    // 头像文件 ID
    avatarFileId?: string
}

export type EmployeePositionLevelResponse = {
    // 职级 ID
    id: number
    // 名称
    name: string
    // 编码
    code: string
}

export type EmployeePositionResponse = {
    // 职务 ID
    id: number
    // 名称
    name: string
    // 编码
    code: string
}

export type EmployeeJobResponse = {
    // 职位 ID
    id: number
    // 名称
    name: string
    // 编码
    code: string
}

export class Employee {
    static updateCurrent = async (request: UpdateCurrentEmployeeRequest) => {
        return await HttpUtil.post<ResultResponse>('/system/employee/updateCurrent', request)
    }

    static modifyCurrentPassword = async (request: ModifyCurrentEmployeePasswordRequest) => {
        return await HttpUtil.post<ResultResponse>('/system/employee/modifyCurrentPassword', request)
    }

    static modifyPassword = async (request: ModifyEmployeePasswordRequest) => {
        return await HttpUtil.post<ResultResponse>('/system/employee/modifyPassword', request)
    }

    static getToken = async (request: ExternalGetTokenRequest) => {
        return await HttpUtil.post<ResultResponse<ExternalGetTokenResponse>>(
            '/external/system/employee/getToken',
            request,
        )
    }

    static getCurrent = async () => {
        return await HttpUtil.post<ResultResponse<GetCurrentEmployeeResponse>>('/system/employee/getCurrent')
    }

    static page = async (request: PageEmployeeRequest) => {
        return await HttpUtil.post<ResultResponse<PageResponse<PageEmployeeResponse>>>('/system/employee/page', request)
    }

    static list = async (request: PageEmployeeRequest) => {
        return await HttpUtil.post<ResultResponse<PageResponse<PageEmployeeResponse>>>('/system/employee/list', request)
    }

    static add = async (request: AddEmployeeRequest) => {
        return await HttpUtil.post<ResultResponse>('/system/employee/add', request)
    }

    static async get(request: GetEmployeeRequest) {
        return await HttpUtil.post<ResultResponse<GetEmployeeResponse>>('/system/employee/get', request)
    }

    static update = async (request: UpdateEmployeeRequest) => {
        return await HttpUtil.post<ResultResponse>('/system/employee/update', request)
    }

    static remove = async (request: DeleteEmployeeRequest) => {
        return await HttpUtil.post<ResultResponse>('/system/employee/delete', request)
    }
}
